import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, CircleCheckBig, Globe, Lock, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Page } from "@/app/Page";
import { useEnvName, useProjectSlug } from "@/app/context";
import { Badge, Button, Callout, Card, ConfirmTyped, EmptyState, ErrorState, ExplainPanel, RelativeTime, ShowAsCode, Skeleton, StatusBadge, describeStatus, toast } from "@/design";
import { ApiError, api, idem, qk, unwrap, useDomain, useServiceInstances, useVerifyDomain, type Domain } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Checks } from "../-domains/Checks";
import { RecordCard, RecordsGuide } from "../-domains/RecordsGuide";
import { registrarFor, REGISTRARS, type RegistrarId } from "../-domains/registrars";
import { Stepper } from "../-domains/Stepper";

export const Route = createFileRoute("/$workspace/$project/domains/$domain")({
  staticData: { title: "Domain" },
  component: DomainScreen,
});

function DomainScreen() {
  const project = useProjectSlug();
  const env = useEnvName();
  const { domain: host } = Route.useParams();
  const [poll, setPoll] = useState<number | false>(3000);
  const q = useDomain(project, env, host, { refetchInterval: poll });
  const d = q.data;

  useEffect(() => {
    if (!d) return;
    setPoll(d.status === "active" || d.kind === "generated" ? false : d.status === "orphaned" ? 30_000 : 3000);
  }, [d]);

  return (
    <Page width="default">
      <Link
        to="/$workspace/$project/settings/domains"
        params={(p) => p as never}
        search={(s) => ({ env: (s as { env?: string }).env }) as never}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" /> Domains
      </Link>
      {q.error ? (
        q.error instanceof ApiError && q.error.status === 404 ? (
          <EmptyState
            size="lg"
            icon={<Globe />}
            title={`${host} is not connected to this environment`}
            description={`It may have been removed, or it belongs to another environment than ${env}.`}
            actions={
              <Button asChild variant="primary">
                <Link to="/$workspace/$project/domains/new" params={(p) => p as never} search={(s) => ({ env: (s as { env?: string }).env, host }) as never}>
                  Add {host}
                </Link>
              </Button>
            }
          />
        ) : (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        )
      ) : !d ? (
        <DomainSkeleton />
      ) : (
        <DomainView d={d} project={project} env={env} polling={poll !== false} isFetching={q.isFetching} />
      )}
    </Page>
  );
}

function DomainView({ d, project, env, polling, isFetching }: { d: Domain; project: string; env: string; polling: boolean; isFetching: boolean }) {
  const instances = useServiceInstances(project, env);
  const svc = instances.data?.data.find((i) => i.service_id === d.service_id);
  const detected = d.registrar?.name ?? null;
  const [regId, setRegId] = useState<RegistrarId>(() => registrarFor(detected).id);
  const reg = REGISTRARS.find((r) => r.id === regId)!;
  const verify = useVerifyDomain(project, env);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const wasActive = useRef(d.status === "active");
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    if (d.status === "active" && !wasActive.current) setCelebrate(true);
    wasActive.current = d.status === "active";
  }, [d.status]);

  const remove = useMutation({
    mutationFn: () =>
      unwrap(api.DELETE("/v1/projects/{project}/environments/{environment}/domains/{domain}", { params: { path: { project, environment: env, domain: d.hostname }, header: idem() } })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.domains(project, env) });
      toast.success(`${d.hostname} removed`, { description: "Remember to delete its DNS records at your registrar too." });
      void navigate({ to: "/$workspace/$project/settings/domains", params: (p: Record<string, string>) => p as never, search: (s: Record<string, unknown>) => ({ env: s.env }) as never });
    },
  });

  const info = describeStatus("domain", d.status);
  const setupMode = d.kind === "custom" && d.status !== "active" && d.status !== "orphaned";
  const dnsOk = d.checks?.dns === "ok";
  const visit = `https://${d.hostname}`;

  const checkNow = () =>
    verify.mutate(d.hostname, {
      onSuccess: (res) => {
        void qc.setQueryData(qk.domain(project, env, d.hostname), res);
        toast.info("Checked just now", { description: describeStatus("domain", res.status).explain });
      },
      onError: (e) => toast.error(e),
    });

  return (
    <>
      {setupMode && <Stepper current={dnsOk ? 2 : 1} />}

      <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-1.5 text-sm font-medium text-subtle">{d.kind === "generated" ? "Your free Guv address" : "Custom domain"}</div>
          <h1 className="break-all font-display text-3xl font-semibold tracking-tight text-text">{d.hostname}</h1>
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-sm text-muted">
            <StatusBadge kind="domain" status={d.status} variant="pill" />
            {svc && (
              <span>
                Sends visitors to <strong className="font-medium text-text">{svc.name}</strong>
              </span>
            )}
            {d.last_checked_at && d.status !== "active" && (
              <span className="text-subtle">
                <span className="hidden sm:inline" aria-hidden="true">· </span>Checked <RelativeTime value={d.last_checked_at} />
              </span>
            )}
          </div>
        </div>
        {d.status === "active" && (
          <Button asChild variant="secondary" iconRight={<ArrowUpRight />}>
            <a href={visit} target="_blank" rel="noreferrer">
              Visit site
            </a>
          </Button>
        )}
      </header>

      {d.status === "orphaned" && (
        <Callout tone="danger" className="mb-6" title={d.error?.message ?? info.explain} actions={<Button size="sm" icon={<RefreshCw />} loading={verify.isPending} onClick={checkNow}>Check again</Button>}>
          <p>{d.error?.fix ?? "Point the record back at Guv, or remove this domain."}</p>
          {d.release_at && (
            <p className="mt-1 text-sm">
              We stopped serving it <RelativeTime value={d.orphaned_at} />. If nothing changes, we release it on <strong>{formatDate(d.release_at)}</strong> so nobody else can take it over through your DNS.
            </p>
          )}
        </Callout>
      )}

      {(d.status === "active" || celebrate) && d.kind === "custom" && (
        <Card className="mb-6 overflow-hidden">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
            <span className="flex size-12 shrink-0 animate-pop-in items-center justify-center rounded-full bg-ok-soft text-ok-text">
              <CircleCheckBig className="size-6" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-text">{celebrate ? "It is live. Nice work." : "Live with HTTPS"}</h2>
              <p className="text-base text-muted">
                Visitors to <span className="font-medium text-text">{d.hostname}</span> reach {svc?.name ?? "your app"} over a secure connection.
                {d.certificate?.expires_at && (
                  <>
                    {" "}
                    The certificate from {d.certificate.issuer ?? "Let's Encrypt"} renews on its own before {formatDate(d.certificate.expires_at)}.
                  </>
                )}
              </p>
            </div>
          </div>
        </Card>
      )}

      {d.kind === "custom" && (
        <Card className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3.5">
            <h2 className="flex items-center gap-1.5 text-md font-semibold text-text">
              {setupMode ? "Live verification" : "Checks"}
              <ExplainPanel title="What are we checking?">
                <p>
                  <strong>DNS</strong>: your records point at Guv. <strong>Ownership</strong>: the TXT code proves the domain is yours, so nobody can attach a domain they do not own.
                </p>
                <p>
                  <strong>Certificate</strong>: we get a free HTTPS certificate. <strong>Live</strong>: traffic switches over once everything is ready.
                </p>
              </ExplainPanel>
            </h2>
            <div className="flex items-center gap-2 text-sm text-muted">
              {polling && setupMode && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-1.5 animate-pulse rounded-full bg-accent" aria-hidden="true" />
                  {isFetching ? "Checking…" : "Checking every few seconds"}
                </span>
              )}
              <Button size="sm" variant="ghost" icon={<RefreshCw className={verify.isPending ? "animate-spin" : undefined} />} onClick={checkNow} disabled={verify.isPending}>
                Check now
              </Button>
            </div>
          </div>
          <Checks domain={d} className="px-5 py-6 sm:px-8" />
          {setupMode && d.status_text && <p className="border-t border-border px-5 py-3 text-sm text-muted">{d.status_text}</p>}
        </Card>
      )}

      {d.caa_warning && (
        <Callout tone="warn" className="mb-6" title="Your CAA records block our certificate authority">
          {d.caa_warning}
        </Callout>
      )}

      {d.kind === "generated" ? (
        <Card className="p-5 sm:p-6">
          <div className="flex gap-3">
            <Lock className="mt-0.5 size-5 shrink-0 text-ok-text" />
            <div>
              <h2 className="text-md font-semibold text-text">Always on, always HTTPS</h2>
              <p className="mt-1 text-base text-muted">
                Every web service gets a free address like this one. It keeps working after you add your own domain, which is handy for testing. It cannot be removed while the service exists.
              </p>
            </div>
          </div>
        </Card>
      ) : setupMode || d.status === "orphaned" ? (
        <section aria-labelledby="records-heading">
          <h2 id="records-heading" className="mb-4 text-lg font-semibold text-text">
            {d.status === "orphaned" ? "Fix your DNS" : dnsOk ? "Your records" : "Add these records"}
          </h2>
          <RecordsGuide domain={d} reg={reg} onRegistrar={setRegId} detected={detected} polling={polling} />
        </section>
      ) : (
        <section aria-labelledby="records-heading">
          <h2 id="records-heading" className="mb-4 text-lg font-semibold text-text">
            DNS records
          </h2>
          <p className="-mt-2 mb-4 text-sm text-muted">Keep these in place. If they change, we warn you before anything stops working.</p>
          <div className="space-y-3">
            {(d.dns_records ?? []).map((r) => (
              <RecordCard key={`${r.type}-${r.name}`} record={r} domain={d} reg={reg} />
            ))}
          </div>
          {d.registrar?.name && (
            <p className="mt-3 text-sm text-muted">
              Managed at <Badge variant="outline">{d.registrar.name}</Badge>
            </p>
          )}
        </section>
      )}

      {d.kind === "custom" && (
        <div className="mt-10 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-text">{setupMode ? "Changed your mind?" : "Remove this domain"}</h2>
            <p className="text-sm text-muted">Visitors to {d.hostname} will stop reaching your app. Your free {svc?.name ?? "app"} address keeps working.</p>
          </div>
          <div className="flex items-center gap-2">
            <ShowAsCode
              title={`Manage ${d.hostname}`}
              cli={`guv domains verify ${d.hostname} --env ${env}\nguv domains list --env ${env}\n# guv domains remove ${d.hostname} --env ${env}`}
              apiCall={{ method: "POST", path: `/v1/projects/${project}/environments/${env}/domains/${d.hostname}/verify` }}
            />
            <Button variant="danger-ghost" icon={<Trash2 />} onClick={() => setConfirmRemove(true)}>
              Remove domain
            </Button>
          </div>
        </div>
      )}

      <ConfirmTyped
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={`Remove ${d.hostname}?`}
        consequences={[
          `Visitors to ${d.hostname} will get an error straight away.`,
          "We stop renewing its certificate.",
          "Your DNS records stay at your registrar. Delete them there too, so nobody else can point them somewhere.",
        ]}
        confirmText={d.hostname}
        actionLabel="Remove domain"
        onConfirm={() => remove.mutateAsync()}
      />
    </>
  );
}

function DomainSkeleton() {
  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">Loading domain</span>
      <Skeleton className="h-4 w-28" />
      <Skeleton className="mt-3 h-9 w-2/3" />
      <Skeleton className="mt-3 h-5 w-48" />
      <Skeleton className="mt-8 h-40 w-full rounded-xl" />
      <Skeleton className="mt-6 h-64 w-full rounded-xl" />
    </div>
  );
}
